import type { ShaderDataTypeKeys, Nullable } from "models-webgl2";
import { Webgl2 } from "../webgl2";
import { BufferElement, BufferLayout, VertexBuffer, IndexBuffer } from "./buffer";
import { VertexArray } from "./vertex-array";
import { Mesh2dMap } from "./mesh-2d.types";
import { getQuadVertices, getQuadIndexedVertices } from "./mesh-2d.utils";

export type Mesh2dData = ArrayBufferLike;

export type Mesh2dTextureData = ArrayBufferLike;

type VertexBuffersType = {
  mesh: VertexBuffer;
  texture: VertexBuffer;
};

type BufferAttribute = {
  name: string;
  type: ShaderDataTypeKeys;
};

const meshBufferAttribute: BufferAttribute = {
  name: "a_position",
  type: "vec2",
};

const textureBufferAttribute: BufferAttribute = {
  name: "a_texCoord",
  type: "vec2",
};

export class Mesh2d {
  private readonly _mesh: Mesh2dData;
  private readonly _textureData: Mesh2dTextureData;
  private readonly _indices: Nullable<number[]>;

  public get isIndexed() {
    return !!this._indices?.length;
  }

  public get count() {
    return this._indices?.length ?? this._mesh.byteLength / 8;
  }

  private _vertexBuffers: VertexBuffersType;

  private _vertexArray: VertexArray | null;

  public drawMode: number;

  constructor(
    mesh: Mesh2dData,
    textureData: Mesh2dTextureData = new Float32Array([0, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0]),
    indices?: number[],
  ) {
    this._mesh = mesh;
    this._textureData = textureData;
    this._indices = indices;
  }

  public bind() {
    if (!this._vertexArray) this.createVAO();
    this._vertexArray?.bind();
  }

  public unbind() {
    this._vertexArray?.unbind();
  }

  private createVAO() {
    this.drawMode = Webgl2.gl.TRIANGLES;

    const meshVertexBuffer = new VertexBuffer(
      Webgl2.gl,
      this._mesh,
      new BufferLayout([new BufferElement(meshBufferAttribute.name, meshBufferAttribute.type)]),
    );

    const textureVertexBuffer = new VertexBuffer(
      Webgl2.gl,
      this._textureData,
      new BufferLayout([
        new BufferElement(textureBufferAttribute.name, textureBufferAttribute.type),
      ]),
    );

    this._vertexBuffers = {
      mesh: meshVertexBuffer,
      texture: textureVertexBuffer,
    };

    let indexBuffer: IndexBuffer | undefined;
    if (this._indices) {
      indexBuffer = new IndexBuffer(Webgl2.gl, this._indices);
    }

    this._vertexArray = new VertexArray(Webgl2.gl, Object.values(this._vertexBuffers), indexBuffer);
  }

  public static meshesMap: Mesh2dMap = {} as any;

  public static initializeMeshes() {
    this.meshesMap.rectMesh = new Mesh2d(getQuadVertices(1, 1));
    this.meshesMap.rectIndexedMesh = new Mesh2d(
      getQuadIndexedVertices(1, 1),
      getQuadIndexedVertices(1, 1),
      [
        0,
        1,
        2, // first triangle
        0,
        2,
        3, // second triangle
      ],
    );
  }
}

export default Mesh2d;
